import { CircleAlert, CircleCheck, Info, LoaderCircle, X } from 'lucide-react'
import React from 'react'
import { Toaster as SonnerToaster, toast as sonner } from 'sonner'
import {
  MAX_VISIBLE_TOASTS,
  toastController,
  type ToastEntry,
  type ToastFn,
  type ToastInput,
  type ToastTone,
} from './toast-controller.js'

export type { ToastAction, ToastFn, ToastInput, ToastTone } from './toast-controller.js'

// One toast for web and desktop. `toastController` decides what exists and for
// how long; sonner only lays the cards out (portal, stacking, swipe). Look and
// numbers: Open Design mobile-app-toast.html + css/kiao-mobile.css「Toast」.

export type ToastOptions = Pick<ToastInput, 'id' | 'detail' | 'action' | 'duration'>

const withTone =
  (tone: ToastTone) =>
  (title: string, options?: ToastOptions): string =>
    toastController.show({ ...options, tone, title })

/** Imperative entry point, for code that has no hook (event handlers, `use-*` helpers). */
export const toast = {
  success: withTone('ok'),
  error: withTone('err'),
  info: withTone('info'),
  /** A neutral note; shown as `info`. */
  message: withTone('info'),
  /** Stays up until the same `id` is shown again as success / error, or dismissed. */
  loading: (title: string, options?: ToastOptions): string =>
    toastController.show({ ...options, tone: 'info', title, loading: true }),
  dismiss: (id?: string): void => toastController.dismiss(id),
}

export function useToast(): ToastFn {
  return toastFn
}

const toastFn: ToastFn = (input) => toastController.show(input)

// ── view ────────────────────────────────────────────────────────────────

interface ToastLabels {
  dismissLabel: string
}

const ToastLabelsContext = React.createContext<ToastLabels>({
  dismissLabel: 'Dismiss notification',
})

const ICONS = {
  ok: CircleCheck,
  info: Info,
  err: CircleAlert,
} as const

function ToastCard({ id, initial }: { id: string; initial: ToastEntry }) {
  const { dismissLabel } = React.useContext(ToastLabelsContext)
  const current = React.useSyncExternalStore(
    toastController.subscribe,
    () => toastController.get(id),
    () => undefined
  )
  // Once the controller lets go of a toast, sonner still animates it out. Keep
  // showing what it last said instead of collapsing to an empty card.
  const lastSeen = React.useRef(initial)
  if (current) lastSeen.current = current
  const entry = lastSeen.current

  const cardRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    const surface = cardRef.current?.closest<HTMLElement>('[data-sonner-toast]')
    if (!surface) return
    const hold = (source: 'focus' | 'press', held: boolean) => () =>
      toastController.hold(id, source, held)
    const focusIn = hold('focus', true)
    const focusOut = hold('focus', false)
    const pressIn = hold('press', true)
    const pressOut = hold('press', false)
    surface.addEventListener('focusin', focusIn)
    surface.addEventListener('focusout', focusOut)
    surface.addEventListener('pointerdown', pressIn)
    surface.addEventListener('pointerup', pressOut)
    surface.addEventListener('pointercancel', pressOut)
    return () => {
      surface.removeEventListener('focusin', focusIn)
      surface.removeEventListener('focusout', focusOut)
      surface.removeEventListener('pointerdown', pressIn)
      surface.removeEventListener('pointerup', pressOut)
      surface.removeEventListener('pointercancel', pressOut)
    }
  }, [id])

  const Icon = entry.loading ? LoaderCircle : ICONS[entry.tone]
  const iconKind = entry.loading ? 'loading' : entry.tone
  const { action } = entry

  return (
    <div
      ref={cardRef}
      className="kiao-toast"
      role={entry.tone === 'err' ? 'alert' : 'status'}
      data-tone={entry.tone}
      data-loading={entry.loading ? 'true' : undefined}
      onPointerEnter={() => toastController.hold(id, 'pointer', true)}
      onPointerLeave={() => toastController.hold(id, 'pointer', false)}
    >
      <Icon aria-hidden="true" className="kiao-toast-icon" data-toast-icon={iconKind} />
      <div className="kiao-toast-body">
        <div className="kiao-toast-title" data-toast-title="">
          {entry.title}
        </div>
        {entry.detail ? (
          <div className="kiao-toast-detail" data-toast-detail="">
            {entry.detail}
          </div>
        ) : null}
      </div>
      {action ? (
        <button
          type="button"
          className="kiao-toast-action"
          onClick={() => {
            action.onClick()
            toastController.dismiss(id)
          }}
        >
          {action.label}
        </button>
      ) : null}
      <button
        type="button"
        className="kiao-toast-close"
        aria-label={dismissLabel}
        onClick={() => toastController.dismiss(id)}
      >
        <X aria-hidden="true" width={14} height={14} />
      </button>
    </div>
  )
}

/** Phones and narrow windows: bottom centre instead of bottom right. */
const NARROW_QUERY = '(max-width: 639px)'

function subscribeNarrow(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const query = window.matchMedia(NARROW_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function useNarrowViewport(): boolean {
  return React.useSyncExternalStore(
    subscribeNarrow,
    () =>
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia(NARROW_QUERY).matches
        : false,
    () => false
  )
}

// Toast state is global, so a second mounted host would draw every toast a second
// time and handle Escape twice. Only the earliest-mounted host does anything; the
// rest stay empty and take over if it goes away.
const hosts: object[] = []
const hostListeners = new Set<() => void>()

const hostRegistry = {
  subscribe(listener: () => void): () => void {
    hostListeners.add(listener)
    return () => {
      hostListeners.delete(listener)
    }
  },
  primary: (): object | null => hosts[0] ?? null,
  join(host: object): () => void {
    hosts.push(host)
    for (const listener of [...hostListeners]) listener()
    return () => {
      hosts.splice(hosts.indexOf(host), 1)
      for (const listener of [...hostListeners]) listener()
      // Nothing is left to show what remains once the last host is gone. Wait a
      // tick so React's development double-mount (unmount, mount again) does not count.
      queueMicrotask(() => {
        if (hosts.length === 0) toastController.reset()
      })
    }
  },
}

const OPEN_MODAL_SELECTOR = 'dialog[open], [aria-modal="true"]'

function isInsideDialog(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('dialog, [role="dialog"]') !== null
}

const TOASTER_STYLE = { '--width': '400px' } as React.CSSProperties

export interface ToasterProps {
  /** Accessible name of the ✕ button. */
  dismissLabel?: string
  /** Accessible name of the region that holds the toasts. */
  regionLabel?: string
}

/**
 * The one place toasts are drawn. Mount it once per app; it also wires Escape
 * (closes the newest toast) and pauses timers while the page is in the background.
 */
export function Toaster({
  dismissLabel = 'Dismiss notification',
  regionLabel = 'Notifications',
}: ToasterProps) {
  const entries = React.useSyncExternalStore(
    toastController.subscribe,
    toastController.getVisible,
    toastController.getVisible
  )
  const narrow = useNarrowViewport()
  const self = React.useRef<object>({})
  const primary = React.useSyncExternalStore(
    hostRegistry.subscribe,
    hostRegistry.primary,
    () => null
  )
  const isPrimary = primary === self.current
  const shown = React.useRef(new Map<string, number>())
  const owner = React.useRef<object | null>(null)

  React.useEffect(() => hostRegistry.join(self.current), [])

  React.useEffect(() => {
    if (!isPrimary) return
    const token = {}
    owner.current = token
    const known = shown.current
    return () => {
      // Sonner keeps its own copy of what it was told; forgetting ours makes the
      // next primary host redraw everything the controller still holds.
      known.clear()
      owner.current = null
    }
  }, [isPrimary])

  React.useEffect(() => {
    if (!isPrimary) return
    const known = shown.current
    const token = owner.current
    const alive = new Set<string>()
    for (const entry of entries) {
      alive.add(entry.id)
      if (known.get(entry.id) === entry.revision) continue
      known.set(entry.id, entry.revision)
      sonner.custom(() => <ToastCard id={entry.id} initial={entry} />, {
        id: entry.id,
        duration: Infinity,
        onDismiss: () => {
          if (owner.current === token) toastController.dismiss(entry.id)
        },
      })
    }
    for (const id of [...known.keys()]) {
      if (alive.has(id)) continue
      known.delete(id)
      sonner.dismiss(id)
    }
  }, [entries, isPrimary])

  React.useEffect(() => {
    if (!isPrimary) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      // Escape belongs to whatever modal or popup is open: a <dialog> closes on
      // its own `cancel` event, which does not call preventDefault on the key.
      if (isInsideDialog(event.target) || document.querySelector(OPEN_MODAL_SELECTOR)) return
      toastController.dismissNewest()
    }
    const onVisibility = () => toastController.setPageHidden(document.hidden)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('visibilitychange', onVisibility)
    onVisibility()
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isPrimary])

  const labels = React.useMemo(() => ({ dismissLabel }), [dismissLabel])

  if (!isPrimary) return null

  return (
    <ToastLabelsContext.Provider value={labels}>
      <SonnerToaster
        className="kiao-toaster"
        theme="light"
        position={narrow ? 'bottom-center' : 'bottom-right'}
        expand
        // One more than we show: a toast that is animating out still occupies a slot.
        visibleToasts={MAX_VISIBLE_TOASTS + 1}
        gap={10}
        offset={24}
        mobileOffset={16}
        style={TOASTER_STYLE}
        containerAriaLabel={regionLabel}
      />
    </ToastLabelsContext.Provider>
  )
}

/** Renders the children and the one toast host. */
export function ToastProvider({
  children,
  dismissLabel,
  regionLabel,
}: ToasterProps & { children?: React.ReactNode }) {
  return (
    <>
      {children}
      <Toaster dismissLabel={dismissLabel} regionLabel={regionLabel} />
    </>
  )
}
